import { ColorContext } from 'App';
import { usePerms } from 'contexts/PermsContext';
import * as d3 from 'd3';
import useWindow from 'hooks/useWindow';
import 'leaflet/dist/leaflet.css';
import { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp } from 'react-icons/fa6';
import { MapContainer, Polygon, TileLayer, useMap } from 'react-leaflet';
import { useSetAlertEndMutation } from 'services/alerts/alerts.api';
import { splitByCharacter } from 'utils/stringUtils';
import AlertWidgetRow from './AlertWidgetRow';

export function AlertCard({
  widget,
  dimensions,
  index,
  alert,
  getAlerts,
  canCloseAlert,
  selectedAlert,
  setSelectedAlert,
  setMapCenter,
  setMapPolygon,
  onClose,
}) {
  const [showInfo, setShowInfo] = useState(false);
  const theme = useContext(ColorContext);
  const { width, height } = useWindow();
  const perms = usePerms();
  const [setAlertEnd] = useSetAlertEndMutation();

  const colorScale = d3.scaleLinear().domain([0, 66, 100]).range(['#CD2C2E', '#EE8918', '#34A100']);

  useEffect(() => {
    if (index === 0) {
      if (alert.processedPolygon && alert.processedCenter && setSelectedAlert) {
        setSelectedAlert(alert);
        setMapCenter(alert.processedCenter);
        setMapPolygon(alert.processedPolygon);
      }
    }
  }, [alert.processedPolygon, alert.processedCenter, setSelectedAlert]);

  const closeAlert = async (e) => {
    e?.stopPropagation();

    if (!perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteAlerts'])) return;

    try {
      await setAlertEnd(alert.message_id, parseInt(alert.workplaceId)).unwrap();
    } catch (e) {}

    onClose();
  };

  function ChangeView({ center, bounds }) {
    const map = useMap();
    useEffect(() => {
      if (!map || !center || !bounds) return;

      // Add a small delay to ensure map is fully ready
      const timeoutId = setTimeout(() => {
        try {
          // Check if map container and required internal structures exist
          const container = map.getContainer();
          if (!container || !map._loaded || !map._mapPane) return;

          // Verify map panes exist before trying to use them
          if (!map._proxy || !map._sizeChanged) {
            // Map is not ready, skip this update
            return;
          }

          // Safely calculate zoom level
          const zoom = map.getBoundsZoom(bounds);
          if (zoom !== null && zoom !== undefined && !isNaN(zoom)) {
            map.setView(center, zoom);
          }
        } catch (error) {
          // Silently catch errors during map transitions or unmounting
          // This is expected during rapid component updates
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }, [map, center, bounds]);

    return null;
  }

  function getSeverityLabel(severity) {
    if (severity >= 0 && severity <= 24) return 'High';
    if (severity >= 25 && severity <= 49) return 'Med-High';
    if (severity >= 50 && severity <= 74) return 'Med';
    if (severity >= 75 && severity <= 100) return 'Low';
  }

  if (!widget) {
    return (
      <div className="d-flex flex-column flex-md-row rounded-4 mt-3 w-100 bg-primary">
        {/* Alert Severity Color Tab */}
        <div
          className="w-100 px-3 py-3"
          style={{
            backgroundColor: colorScale(alert.severity),
            maxWidth: width >= 768 ? 20 : '100%',
            borderTopLeftRadius: 15,
            borderTopRightRadius: width >= 768 ? 0 : 15,
            borderBottomLeftRadius: width >= 768 ? 15 : 0,
            marginRight: 20,
          }}
        />
        <div className="w-100 py-3 px-3">
          <div className="w-100">
            <div
              onClick={() => setShowInfo(!showInfo)}
              className="d-flex flex-column flex-lg-row w-10 justify-content-between align-items-center cursor-pointer gap-3"
              style={{ borderBottom: showInfo && 'solid grey 1px' }}
            >
              {/* Card Info */}
              <div className="d-flex flex-column justify-content-center text-center text-lg-start">
                <div className="fs-5 fw-bold">{alert.type}</div>
                <div className="fs-5 mt-3">{alert.sender}</div>
                <div className="fs-5">
                  In Effect {alert.sentTime} - {alert.endTime}
                </div>
                {alert.processedCenter && (
                  <div className="fs-5">
                    Coordinates: {Number(alert.processedCenter[0]).toFixed(5)}° N,{' '}
                    {Number(alert.processedCenter[1]).toFixed(5)}° W
                  </div>
                )}
              </div>
              {/* Map */}
              <div
                key="map"
                className="m-2 ratio"
                style={{ '--bs-aspect-ratio': '50%', maxWidth: 330 }}
                onClick={(e) => e.stopPropagation()}
              >
                {alert.processedCenter &&
                  alert.processedPolygon &&
                  alert.processedPolygon[0][0] !== 0 && (
                    <MapContainer center={alert.processedCenter} zoom={11} className="h-100 w-100">
                      <ChangeView center={alert.processedCenter} bounds={alert.processedPolygon} />
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                      />
                      {alert.processedPolygon.map((poly, index) => (
                        <Polygon key={index} positions={poly} />
                      ))}
                    </MapContainer>
                  )}
              </div>
              {/* Buttons */}
              <div
                className="d-flex flex-column flex-lg-row"
                style={{ width: width < 992 && '100%' }}
              >
                {canCloseAlert &&
                  perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteAlerts']) && (
                    <Button
                      className="rounded-4 p-2 m-1 text-white"
                      style={{
                        width: width >= 992 ? '80px' : '100%',
                      }}
                      onClick={(e) => {
                        if (
                          window.confirm(
                            'Are you sure you want to close this alert? This action cannot be undone.',
                          )
                        ) {
                          closeAlert(e);
                          getAlerts();
                        }
                      }}
                      variant="success"
                    >
                      Close
                    </Button>
                  )}
                {width >= '992' ? (
                  <Button onClick={() => setShowInfo(!showInfo)} variant="secondary">
                    {showInfo ? <FaChevronLeft size={40} /> : <FaChevronRight size={40} />}
                  </Button>
                ) : (
                  <Button onClick={() => setShowInfo(!showInfo)} variant="secondary">
                    {showInfo ? <FaChevronUp size={30} /> : <FaChevronDown size={30} />}
                  </Button>
                )}
              </div>
            </div>
          </div>
          {showInfo && (
            <div className="pe-3">
              <div>
                <div className="my-2 fs-5 fw-bold">
                  {alert.provider?.toLowerCase().includes('fusion') && (
                    <div>
                      <div className="fs-5">{alert.type}</div>
                      <div className="fs-5">Until: {new Date(alert.endTime).toLocaleString()}</div>
                    </div>
                  )}
                  {splitByCharacter(alert.headline, ';').map((line, index) => {
                    if (line.includes('till:')) {
                      // Parse the date string to a Date object
                      const parsedDate = new Date(line.replace('till:', ''));
                      // Get the current year
                      const currentYear = new Date().getFullYear();
                      // Set the year of the parsed date to the current year
                      parsedDate.setFullYear(currentYear);
                      // Convert the modified date back to a locale string
                      const updatedDateLocaleString = parsedDate.toLocaleString();
                      return (
                        <div key={index} className="fs-6">
                          Until: {updatedDateLocaleString}
                        </div>
                      );
                    } else
                      return (
                        <div key={index} className="fs-6">
                          {line}
                        </div>
                      );
                  })}
                </div>
                <ul className="mb-3 fs-6">
                  {splitByCharacter(alert.description, '*').map((line, index) => {
                    if (line)
                      return (
                        <li key={index} className="ms-3 fs-6">
                          {line}
                        </li>
                      );
                  })}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (widget) {
    if (dimensions[0] === 1 && dimensions[1] === 1) {
      return (
        <div className="my-1 overflow-hidden rounded-3">
          <AlertWidgetRow
            variant="1x"
            colorScale={colorScale}
            getSeverityLabel={getSeverityLabel}
            alert={alert}
            canCloseAlert={canCloseAlert}
            closeAlert={closeAlert}
            getAlerts={getAlerts}
          />
        </div>
      );
    }

    if (dimensions[0] === 1 && dimensions[1] === 2) {
      return (
        <div
          className="my-1 rounded-3 overflow-hidden cursor-pointer"
          onClick={() => {
            setSelectedAlert(alert);
            setMapCenter(alert.processedCenter);
            setMapPolygon(alert.processedPolygon);
          }}
          style={{
            backgroundColor: selectedAlert?.message_id === alert.message_id ? theme.primary : '',
          }}
        >
          <AlertWidgetRow
            variant="1x"
            colorScale={colorScale}
            getSeverityLabel={getSeverityLabel}
            alert={alert}
            canCloseAlert={canCloseAlert}
            closeAlert={closeAlert}
            getAlerts={getAlerts}
          />
        </div>
      );
    }

    if (dimensions[0] === 2 && dimensions[1] === 1) {
      return (
        <div className="my-1 overflow-hidden rounded-3">
          <AlertWidgetRow
            variant="2x"
            colorScale={colorScale}
            getSeverityLabel={getSeverityLabel}
            alert={alert}
            canCloseAlert={canCloseAlert}
            closeAlert={closeAlert}
            getAlerts={getAlerts}
          />
        </div>
      );
    }
  }
}
